export type ColiseumDemoVariant = 'coliseum-50k' | 'coliseum-64k';

/** Physics values shared by both deployed Coliseum variants and certification. */
export const COLISEUM_PRODUCTION_PHYSICS_PROFILE = Object.freeze({
  substeps: 1,
  maxPairsPerBodyBroadphase: 16,
  pairManifoldSlots: 8,
  contactPoolMode: 'compact',
  maxContactsPerBodySolver: 64,
  pairSolveColorCount: 8,
  maxJoints: 0,
  maxSprings: 0,
  maxIgnoredPairs: 0,
  // The 50K/64K contact-only path is faster at the full ten certified rounds:
  // fixed scheduling removes the residual clear/reduce/indirect bookkeeping
  // while executing every solver iteration. This increases, never lowers, the
  // amount of AVBD work and keeps the production accuracy ceiling exact.
  avbdAdaptiveIterations: false,
  avbdAdaptiveMinIterations: 8,
  avbdAdaptiveDualTolerance: 0.02,
  avbdAdaptivePenaltyTolerance: 0.08,
  // The official-exact profile retains demo3d gamma=0.999. Deep production
  // stacks use the same AVBD Eq. 19 update with a slower decay so finite
  // one-substep solves do not accumulate load-interface bias.
  avbdPenaltyDecayGamma: 0.9999,
  avbdCacheContactTangentInvLength: true,
  avbdDenseContactSolverState: true,
  avbdSpecializeReferenceFinalizer: true,
  // Precomputing per-body capture deltas is bit-exact, but the extra dispatch
  // costs more than its saved reads in Chromium/WebGPU on the target GPU.
  avbdCaptureBodyDeltaCache: false,
  // Solver-only geometry is copied once after prepare into a tightly packed
  // 80-byte row, then reused by both endpoints through all fixed AVBD rounds.
  avbdPackedContactSolverGeometry: true,
  // The last fixed capture commits the persistent row in-place, removing the
  // standalone finalizer traversal and the dead final hot-sidecar write.
  // The fused final capture is 0-ULP equivalent, but currently regresses the
  // mature 50K contact workload. Retain the diagnostic override while keeping
  // the production Coliseum on the faster standalone commit path.
  avbdFuseFinalCaptureCommit: false,
  // The exact manifold-capture A/B path remains available, but this browser
  // workload is faster with the wider per-contact dispatch on the target GPU.
  avbdManifoldCapture: false,
  // Chromium/WebGPU pays much more scheduling and barrier overhead for the
  // paper's one-workgroup-per-body mapping than the scalar-per-body kernel on
  // this workload. Keep the exact experimental path available for A/B runs,
  // but production selects the faster scalar mapping.
  avbdCooperativeBodySolve: false,
  enableSleeping: true,
  // The Coliseum is one connected contact island: it is either entirely
  // awake (where sparse compaction only adds a 64K-body atomic scan before
  // the same dense traversal) or entirely asleep (where the exact zero-work
  // certificate skips broadphase altogether). Keep the reusable sparse path
  // available for genuinely disconnected scenes, but do not select it here.
  enableSleepingSparseBroadphase: false,
  sleepingSparseMinimumBodyCount: 8192,
  sleepFrames: 60,
  sleepLinearSpeed: 0.025,
  sleepAngularSpeed: 0.025,
  enableBroadphasePairDedup: false,
} as const);

export type ColiseumProductionPhysicsManifest = Readonly<
  typeof COLISEUM_PRODUCTION_PHYSICS_PROFILE & {
    variant: ColiseumDemoVariant;
    maxBodies: number;
    expectedBrickCount: number;
    maxPersistentManifolds: number;
    compactContactPoolCapacity: number;
    solverIterations: number;
  }
>;

type ColiseumVariantConfig = {
  expectedBrickCount: number;
  bottomUnits: number;
  baseInnerRadius: number;
  tangentialPitch: number;
  capPitch: number;
};

export type ColiseumRingKind = 'support' | 'cap';

export type ColiseumRingLayout = {
  kind: ColiseumRingKind;
  level: number;
  course: number;
  unit: number;
  supportIndex: number;
  radius: number;
  y: number;
  brickCount: number;
  angleStep: number;
  phase: number;
};

export type ColiseumLayoutDiagnostics = {
  loadInterfaceCount: number;
  maxRestC0Error: number;
  minCapSeamGap: number;
  minSameRingGap: number;
  minSupportRadialRingGap: number;
  minUpperSupportDeckMargin: number;
};

export type ColiseumLayout = {
  variant: ColiseumDemoVariant;
  collisionMargin: number;
  restGap: number;
  levelCount: number;
  brickCount: number;
  supportBrickCount: number;
  capBrickCount: number;
  rings: readonly ColiseumRingLayout[];
  diagnostics: ColiseumLayoutDiagnostics;
};

/**
 * Returns the exact t=0 brick positions in the same ring/brick order consumed
 * by buildColiseumDemo. The Float32 representation also matches the engine's
 * initial pose storage, so regression drift is measured from construction
 * time instead of from the first already-integrated frame.
 */
export function createColiseumBrickInitialPositions(
  layout: Pick<ColiseumLayout, 'brickCount' | 'rings'>,
): Float32Array {
  const positions = new Float32Array(layout.brickCount * 3);
  let brickIndex = 0;
  for (const ring of layout.rings) {
    for (let brick = 0; brick < ring.brickCount; brick++) {
      const angle = ring.phase + brick * ring.angleStep;
      const offset = brickIndex * 3;
      positions[offset] = Math.cos(angle) * ring.radius;
      positions[offset + 1] = ring.y;
      positions[offset + 2] = Math.sin(angle) * ring.radius;
      brickIndex++;
    }
  }
  if (brickIndex !== layout.brickCount) {
    throw new Error(
      `[ColiseumLayout] expected ${layout.brickCount} initial poses, wrote ${brickIndex}.`,
    );
  }
  return positions;
}

export const COLISEUM_SUPPORT_HALF_EXTENTS: readonly [number, number, number] = [
  0.5,
  1.0 / 12.0,
  1.0 / 6.0,
];

const WALL_UNIT_WIDTH = COLISEUM_SUPPORT_HALF_EXTENTS[0] * 2.0;

// Adjacent radial cap rings have different angular counts and phases. If their
// radial length is exactly one wall unit, their nominally coincident end faces
// cut through one another on the curved deck. Reserve a real seam instead.
export const COLISEUM_CAP_RADIAL_SEAM_CLEARANCE = 0.003;
export const COLISEUM_CAP_HALF_EXTENTS: readonly [number, number, number] = [
  (WALL_UNIT_WIDTH - COLISEUM_CAP_RADIAL_SEAM_CLEARANCE) * 0.5,
  COLISEUM_SUPPORT_HALF_EXTENTS[1],
  COLISEUM_SUPPORT_HALF_EXTENTS[2],
];

const BRICK_WIDTH = COLISEUM_SUPPORT_HALF_EXTENTS[2] * 2.0;
const COURSES_PER_LEVEL = 3;
const SUPPORT_ROW_OFFSETS = [0, 2] as const;
const OUTER_STEPBACK_PER_LEVEL = 0.35;
const INNER_STEPBACK_PER_LEVEL = WALL_UNIT_WIDTH - OUTER_STEPBACK_PER_LEVEL;
const NEAR_RING_SEARCH_RADIUS = 2;
const LAYOUT_EPSILON = 1e-8;

const VARIANT_CONFIGS: Record<ColiseumDemoVariant, ColiseumVariantConfig> = {
  'coliseum-50k': {
    expectedBrickCount: 50224,
    bottomUnits: 9,
    baseInnerRadius: 18.0,
    tangentialPitch: 1.1,
    capPitch: 0.43,
  },
  'coliseum-64k': {
    expectedBrickCount: 64312,
    bottomUnits: 11,
    baseInnerRadius: 14.0,
    tangentialPitch: 1.1,
    capPitch: 0.43,
  },
};

function createProductionPhysicsManifest(
  variant: ColiseumDemoVariant,
  variantPhysics: Pick<
    ColiseumProductionPhysicsManifest,
    'maxBodies' | 'maxPersistentManifolds' | 'compactContactPoolCapacity' | 'solverIterations'
  >,
): ColiseumProductionPhysicsManifest {
  return Object.freeze({
    ...COLISEUM_PRODUCTION_PHYSICS_PROFILE,
    variant,
    expectedBrickCount: VARIANT_CONFIGS[variant].expectedBrickCount,
    ...variantPhysics,
  });
}

/**
 * Complete production-physics manifests consumed by both the live presets and
 * the WebGPU regression worlds. Variant capacities and nonlinear iteration
 * budgets live here so those two entry points cannot silently drift apart.
 */
export const COLISEUM_PRODUCTION_PHYSICS_MANIFESTS: Readonly<
  Record<ColiseumDemoVariant, ColiseumProductionPhysicsManifest>
> = Object.freeze({
  'coliseum-50k': createProductionPhysicsManifest('coliseum-50k', {
    maxBodies: 50688,
    maxPersistentManifolds: 196608,
    compactContactPoolCapacity: 786432,
    // The full 1,470-step certification stays inside its drift contract at
    // the reference ten nonlinear sweeps.
    solverIterations: 10,
  }),
  'coliseum-64k': createProductionPhysicsManifest('coliseum-64k', {
    maxBodies: 64512,
    maxPersistentManifolds: 262144,
    compactContactPoolCapacity: 917504,
    // The deeper aggregate exceeds its p99-displacement gate at ten and
    // eleven sweeps, so retain twelve where the measured margin requires it.
    solverIterations: 12,
  }),
});

type HorizontalObb = {
  centerX: number;
  centerZ: number;
  axisUX: number;
  axisUZ: number;
  axisVX: number;
  axisVZ: number;
  halfU: number;
  halfV: number;
};

function levelUnitCount(config: ColiseumVariantConfig, level: number): number {
  return config.bottomUnits - level;
}

function levelInnerRadius(config: ColiseumVariantConfig, level: number): number {
  return config.baseInnerRadius + level * INNER_STEPBACK_PER_LEVEL;
}

function ringBrickCount(radius: number, pitch: number): number {
  return Math.max(1, Math.floor((2.0 * Math.PI * radius) / pitch));
}

function makeHorizontalObb(ring: ColiseumRingLayout, angle: number): HorizontalObb {
  const radialX = Math.cos(angle);
  const radialZ = Math.sin(angle);
  const tangentX = -radialZ;
  const tangentZ = radialX;
  if (ring.kind === 'support') {
    return {
      centerX: radialX * ring.radius,
      centerZ: radialZ * ring.radius,
      axisUX: tangentX,
      axisUZ: tangentZ,
      axisVX: radialX,
      axisVZ: radialZ,
      halfU: COLISEUM_SUPPORT_HALF_EXTENTS[0],
      halfV: COLISEUM_SUPPORT_HALF_EXTENTS[1],
    };
  }
  return {
    centerX: radialX * ring.radius,
    centerZ: radialZ * ring.radius,
    axisUX: radialX,
    axisUZ: radialZ,
    axisVX: tangentX,
    axisVZ: tangentZ,
    halfU: COLISEUM_CAP_HALF_EXTENTS[0],
    halfV: COLISEUM_CAP_HALF_EXTENTS[2],
  };
}

function projectedRadius(obb: HorizontalObb, axisX: number, axisZ: number): number {
  return obb.halfU * Math.abs(obb.axisUX * axisX + obb.axisUZ * axisZ)
    + obb.halfV * Math.abs(obb.axisVX * axisX + obb.axisVZ * axisZ);
}

// Positive means separated; negative means penetration. For this validation we
// only need a conservative signed SAT result, not the closest-point vector.
function horizontalSignedSeparation(a: HorizontalObb, b: HorizontalObb): number {
  const dx = b.centerX - a.centerX;
  const dz = b.centerZ - a.centerZ;
  const axes = [
    [a.axisUX, a.axisUZ],
    [a.axisVX, a.axisVZ],
    [b.axisUX, b.axisUZ],
    [b.axisVX, b.axisVZ],
  ] as const;
  let separation = Number.NEGATIVE_INFINITY;
  for (const [axisX, axisZ] of axes) {
    const centerDistance = Math.abs(dx * axisX + dz * axisZ);
    separation = Math.max(
      separation,
      centerDistance
        - projectedRadius(a, axisX, axisZ)
        - projectedRadius(b, axisX, axisZ),
    );
  }
  return separation;
}

function ringAngle(ring: ColiseumRingLayout, brick: number): number {
  return ring.phase + brick * ring.angleStep;
}

function minimumSameRingGap(ring: ColiseumRingLayout): number {
  let minimum = Number.POSITIVE_INFINITY;
  for (let brick = 0; brick < ring.brickCount; brick++) {
    const next = (brick + 1) % ring.brickCount;
    minimum = Math.min(
      minimum,
      horizontalSignedSeparation(
        makeHorizontalObb(ring, ringAngle(ring, brick)),
        makeHorizontalObb(ring, ringAngle(ring, next)),
      ),
    );
  }
  return minimum;
}

function minimumInterRingGap(a: ColiseumRingLayout, b: ColiseumRingLayout): number {
  let minimum = Number.POSITIVE_INFINITY;
  for (let brickA = 0; brickA < a.brickCount; brickA++) {
    const angleA = ringAngle(a, brickA);
    const normalizedAngle = ((angleA % (2.0 * Math.PI)) + 2.0 * Math.PI) % (2.0 * Math.PI);
    const nearestB = Math.round((normalizedAngle - b.phase) / b.angleStep);
    const obbA = makeHorizontalObb(a, angleA);
    for (let offset = -NEAR_RING_SEARCH_RADIUS; offset <= NEAR_RING_SEARCH_RADIUS; offset++) {
      const brickB = ((nearestB + offset) % b.brickCount + b.brickCount) % b.brickCount;
      minimum = Math.min(
        minimum,
        horizontalSignedSeparation(
          obbA,
          makeHorizontalObb(b, ringAngle(b, brickB)),
        ),
      );
    }
  }
  return minimum;
}

function getLevelRings(
  layout: Pick<ColiseumLayout, 'rings'>,
  level: number,
  kind: ColiseumRingKind,
  course: number,
): ColiseumRingLayout[] {
  return layout.rings.filter((ring) => (
    ring.level === level && ring.kind === kind && ring.course === course
  ));
}

export function validateColiseumLayout(
  layout: Omit<ColiseumLayout, 'diagnostics'>,
): ColiseumLayoutDiagnostics {
  const config = VARIANT_CONFIGS[layout.variant];
  if (layout.brickCount !== config.expectedBrickCount) {
    throw new Error(
      `[ColiseumLayout] ${layout.variant} expected ${config.expectedBrickCount} bricks, got ${layout.brickCount}.`,
    );
  }
  if (layout.supportBrickCount + layout.capBrickCount !== layout.brickCount) {
    throw new Error('[ColiseumLayout] support/cap brick counts do not sum to the total.');
  }

  const supportHalfHeight = COLISEUM_SUPPORT_HALF_EXTENTS[2];
  const capHalfHeight = COLISEUM_CAP_HALF_EXTENTS[1];
  const loadInterfaceGaps: number[] = [];
  const courseYs: number[][] = [];
  const capYs: number[] = [];
  for (let level = 0; level < layout.levelCount; level++) {
    const levelCourses: number[] = [];
    for (let course = 0; course < COURSES_PER_LEVEL; course++) {
      const rings = getLevelRings(layout, level, 'support', course);
      if (rings.length === 0) {
        throw new Error(`[ColiseumLayout] missing support rings at level=${level}, course=${course}.`);
      }
      const y = rings[0]!.y;
      if (rings.some((ring) => Math.abs(ring.y - y) > LAYOUT_EPSILON)) {
        throw new Error(`[ColiseumLayout] support course is not planar at level=${level}, course=${course}.`);
      }
      levelCourses.push(y);
    }
    const caps = getLevelRings(layout, level, 'cap', -1);
    if (caps.length === 0) {
      throw new Error(`[ColiseumLayout] missing cap rings at level=${level}.`);
    }
    const capY = caps[0]!.y;
    if (caps.some((ring) => Math.abs(ring.y - capY) > LAYOUT_EPSILON)) {
      throw new Error(`[ColiseumLayout] cap deck is not planar at level=${level}.`);
    }
    courseYs.push(levelCourses);
    capYs.push(capY);
  }

  loadInterfaceGaps.push(courseYs[0]![0]! - supportHalfHeight);
  for (let level = 0; level < layout.levelCount; level++) {
    const ys = courseYs[level]!;
    for (let course = 1; course < COURSES_PER_LEVEL; course++) {
      loadInterfaceGaps.push(
        ys[course]! - supportHalfHeight - (ys[course - 1]! + supportHalfHeight),
      );
    }
    loadInterfaceGaps.push(
      capYs[level]! - capHalfHeight - (ys[COURSES_PER_LEVEL - 1]! + supportHalfHeight),
    );
    if (level + 1 < layout.levelCount) {
      loadInterfaceGaps.push(
        courseYs[level + 1]![0]! - supportHalfHeight - (capYs[level]! + capHalfHeight),
      );
    }
  }
  const maxRestC0Error = loadInterfaceGaps.reduce(
    (maximum, gap) => Math.max(maximum, Math.abs(gap + layout.collisionMargin)),
    0.0,
  );
  if (maxRestC0Error > LAYOUT_EPSILON) {
    throw new Error(
      `[ColiseumLayout] load interfaces do not satisfy C0=gap+margin=0 (max error=${maxRestC0Error}).`,
    );
  }

  let minSameRingGap = Number.POSITIVE_INFINITY;
  let minCapSeamGap = Number.POSITIVE_INFINITY;
  let minSupportRadialRingGap = Number.POSITIVE_INFINITY;
  for (const ring of layout.rings) {
    minSameRingGap = Math.min(minSameRingGap, minimumSameRingGap(ring));
  }
  for (let level = 0; level < layout.levelCount; level++) {
    const caps = getLevelRings(layout, level, 'cap', -1);
    for (let ring = 0; ring + 1 < caps.length; ring++) {
      minCapSeamGap = Math.min(
        minCapSeamGap,
        minimumInterRingGap(caps[ring]!, caps[ring + 1]!),
      );
    }
    for (let course = 0; course < COURSES_PER_LEVEL; course++) {
      const supports = getLevelRings(layout, level, 'support', course);
      for (let ring = 0; ring + 1 < supports.length; ring++) {
        minSupportRadialRingGap = Math.min(
          minSupportRadialRingGap,
          minimumInterRingGap(supports[ring]!, supports[ring + 1]!),
        );
      }
    }
  }
  if (minSameRingGap < -LAYOUT_EPSILON) {
    throw new Error(`[ColiseumLayout] a ring self-intersects by ${-minSameRingGap}m.`);
  }
  if (minCapSeamGap < -LAYOUT_EPSILON) {
    throw new Error(`[ColiseumLayout] adjacent cap rings intersect by ${-minCapSeamGap}m.`);
  }
  if (minSupportRadialRingGap < -LAYOUT_EPSILON) {
    throw new Error(
      `[ColiseumLayout] adjacent support rings intersect by ${-minSupportRadialRingGap}m.`,
    );
  }

  let minUpperSupportDeckMargin = Number.POSITIVE_INFINITY;
  for (let level = 1; level < layout.levelCount; level++) {
    const lowerInnerRadius = levelInnerRadius(config, level - 1);
    const lowerUnits = levelUnitCount(config, level - 1);
    const deckInner = lowerInnerRadius + 0.5 - COLISEUM_CAP_HALF_EXTENTS[0];
    const deckOuter = lowerInnerRadius + lowerUnits - 0.5 + COLISEUM_CAP_HALF_EXTENTS[0];
    const upperSupports = getLevelRings(layout, level, 'support', 0);
    for (const support of upperSupports) {
      minUpperSupportDeckMargin = Math.min(
        minUpperSupportDeckMargin,
        support.radius - COLISEUM_SUPPORT_HALF_EXTENTS[1] - deckInner,
        deckOuter - support.radius - COLISEUM_SUPPORT_HALF_EXTENTS[1],
      );
    }
  }
  if (minUpperSupportDeckMargin < -LAYOUT_EPSILON) {
    throw new Error(
      `[ColiseumLayout] an upper support overhangs its lower cap deck by ${-minUpperSupportDeckMargin}m.`,
    );
  }

  return {
    loadInterfaceCount: loadInterfaceGaps.length,
    maxRestC0Error,
    minCapSeamGap,
    minSameRingGap,
    minSupportRadialRingGap,
    minUpperSupportDeckMargin,
  };
}

export function generateColiseumLayout(
  variant: ColiseumDemoVariant,
  collisionMargin: number,
): ColiseumLayout {
  if (!Number.isFinite(collisionMargin) || collisionMargin < 0.0) {
    throw new Error(`[ColiseumLayout] invalid collision margin ${collisionMargin}.`);
  }
  const config = VARIANT_CONFIGS[variant];
  // AVBD stores C0 = gap + collisionMargin for a contact. Building every
  // load-bearing interface at gap=-margin makes the complete support graph
  // active on frame zero with C0=0, instead of dropping each course through a
  // positive construction gap before narrowphase can create its constraint.
  const restGap = -collisionMargin;
  const supportHalfHeight = COLISEUM_SUPPORT_HALF_EXTENTS[2];
  const supportHeight = supportHalfHeight * 2.0;
  const capHalfHeight = COLISEUM_CAP_HALF_EXTENTS[1];
  const capHeight = capHalfHeight * 2.0;
  const levelVerticalStep = COURSES_PER_LEVEL * supportHeight
    + capHeight
    + 4.0 * restGap;
  if (supportHeight + restGap <= 0.0 || capHeight + restGap <= 0.0 || levelVerticalStep <= 0.0) {
    throw new Error(`[ColiseumLayout] collision margin ${collisionMargin} is too large for the bricks.`);
  }

  const rings: ColiseumRingLayout[] = [];
  let brickCount = 0;
  let supportBrickCount = 0;
  let capBrickCount = 0;
  for (let level = 0; level < config.bottomUnits; level++) {
    const unitCount = levelUnitCount(config, level);
    const innerRadius = levelInnerRadius(config, level);
    const levelBaseY = supportHalfHeight + restGap + level * levelVerticalStep;

    for (let course = 0; course < COURSES_PER_LEVEL; course++) {
      const y = levelBaseY + course * (supportHeight + restGap);
      for (let unit = 0; unit < unitCount; unit++) {
        for (let supportIndex = 0; supportIndex < SUPPORT_ROW_OFFSETS.length; supportIndex++) {
          const rowOffset = SUPPORT_ROW_OFFSETS[supportIndex]!;
          const radius = innerRadius
            + BRICK_WIDTH * 0.5
            + (unit * 3 + rowOffset) * BRICK_WIDTH;
          const count = ringBrickCount(radius, config.tangentialPitch);
          const angleStep = (2.0 * Math.PI) / count;
          const phaseScale = ((course + unit + supportIndex) & 1) === 0 ? 0.0 : 0.5;
          rings.push({
            kind: 'support',
            level,
            course,
            unit,
            supportIndex,
            radius,
            y,
            brickCount: count,
            angleStep,
            phase: phaseScale * angleStep,
          });
          brickCount += count;
          supportBrickCount += count;
        }
      }
    }

    const capY = levelBaseY
      + (COURSES_PER_LEVEL - 1) * (supportHeight + restGap)
      + supportHalfHeight
      + restGap
      + capHalfHeight;
    for (let unit = 0; unit < unitCount; unit++) {
      const radius = innerRadius + (unit + 0.5) * WALL_UNIT_WIDTH;
      const count = ringBrickCount(radius, config.capPitch);
      const angleStep = (2.0 * Math.PI) / count;
      const phaseScale = ((level + unit) & 1) === 0 ? 0.0 : 0.5;
      rings.push({
        kind: 'cap',
        level,
        course: -1,
        unit,
        supportIndex: -1,
        radius,
        y: capY,
        brickCount: count,
        angleStep,
        phase: phaseScale * angleStep,
      });
      brickCount += count;
      capBrickCount += count;
    }
  }

  const layoutWithoutDiagnostics: Omit<ColiseumLayout, 'diagnostics'> = {
    variant,
    collisionMargin,
    restGap,
    levelCount: config.bottomUnits,
    brickCount,
    supportBrickCount,
    capBrickCount,
    rings,
  };
  return {
    ...layoutWithoutDiagnostics,
    diagnostics: validateColiseumLayout(layoutWithoutDiagnostics),
  };
}
